/**
 * Дерево проектов в левой панели.
 *
 * Иерархия приходит с сервера плоским списком с материализованным путём, и
 * дерево собирается из него на клиенте: сервер не должен отдавать вложенный
 * JSON, чтобы фильтр «проект и все вложенные» оставался одним запросом.
 *
 * Проект можно завести, переименовать, завершить и убрать в корзину — всё
 * из меню на самой строке. Раньше была только кнопка «создать», и заведённый
 * по ошибке проект оставался в панели навсегда.
 *
 * Щелчок по названию отбирает задачи проекта, а «Обзор проекта» из меню
 * открывает его карточку: это два разных действия, и объединять их нельзя —
 * в панель заходят прежде всего за списком задач.
 */

import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  LayoutDashboard,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2,
} from "lucide-react";

import { filterTree } from "@/modules/projects/tree-filter";
import * as work from "@/shared/api/work";
import { cn } from "@/shared/lib/cn";
import { Button, IconButton } from "@/shared/ui/Button";
import { useConfirm } from "@/shared/ui/Confirm";
import { SkeletonRows } from "@/shared/ui/Feedback";
import { Dialog, DropdownMenu } from "@/shared/ui/Overlays";
import { TextField } from "@/shared/ui/TextField";
import { useToast } from "@/shared/ui/Toast";

export interface ProjectTreeProps {
  selectedId: number | null;
  onSelect: (projectId: number | null) => void;
  /** Открыть обзор проекта: карточка, а не отбор задач по нему. */
  onOpen: (projectId: number) => void;
}

interface Node extends work.Project {
  children: Node[];
}

function build(projects: work.Project[]): Node[] {
  const nodes = new Map<number, Node>();
  for (const project of projects) {
    nodes.set(project.id, { ...project, children: [] });
  }
  const roots: Node[] = [];
  for (const node of nodes.values()) {
    const parent = node.parent_id === null ? null : nodes.get(node.parent_id);
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/** Порог, после которого список стоит фильтровать, а не листать. */
const SEARCH_FROM = 12;

/** Что сейчас правится или заводится: одно окно на оба случая. */
interface Draft {
  name: string;
  /** Проект, который переименовывают. `null` — заводится новый. */
  editing: work.Project | null;
  /** Куда положить новый проект. */
  parentId: number | null;
}

export function ProjectTree({
  selectedId,
  onSelect,
  onOpen,
}: ProjectTreeProps): React.ReactElement {
  const client = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [collapsed, setCollapsed] = React.useState<Set<number>>(new Set());
  const [showClosed, setShowClosed] = React.useState(false);
  const [needle, setNeedle] = React.useState("");
  const [draft, setDraft] = React.useState<Draft | null>(null);

  const projects = useQuery({
    queryKey: ["projects", showClosed],
    queryFn: () => work.listProjects(showClosed, showClosed),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ["projects"] });
  };

  const complain = (title: string) => (error: Error) =>
    toast.show({ tone: "danger", title, description: error.message });

  const save = useMutation({
    mutationFn: (input: Draft) =>
      input.editing === null
        ? work.createProject({ name: input.name, parent_id: input.parentId })
        : work.renameProject(input.editing.id, input.name),
    onSuccess: () => {
      setDraft(null);
      refresh();
    },
    onError: complain("Проект не сохранён"),
  });

  const archive = useMutation({
    mutationFn: (input: { id: number; archived: boolean }) =>
      work.archiveProject(input.id, input.archived),
    onSuccess: refresh,
    onError: complain("Не получилось"),
  });

  const remove = useMutation({
    mutationFn: (projectId: number) => work.deleteProject(projectId),
    onSuccess: (_result, projectId) => {
      if (selectedId === projectId) {
        // Выбранный проект уехал в корзину: возвращаем список ко всем задачам,
        // иначе экран остаётся с отбором по тому, чего больше нет
        onSelect(null);
      }
      refresh();
    },
    onError: complain("Проект не убран"),
  });

  const restore = useMutation({
    mutationFn: (projectId: number) => work.restoreProject(projectId),
    onSuccess: refresh,
    onError: complain("Проект не восстановлен"),
  });

  if (projects.isPending) {
    return <SkeletonRows count={3} />;
  }

  const all = projects.data ?? [];
  const trimmed = needle.trim();
  const tree = filterTree(build(all), trimmed);

  function renderNode(node: Node, level: number): React.ReactElement {
    const isCollapsed = collapsed.has(node.id);
    const inactive = node.is_deleted || node.is_archived;

    return (
      <li key={node.id}>
        <div
          className={cn(
            "group flex h-[var(--pt-row-h)] items-center gap-[2px] rounded-[var(--pt-radius-3)] pr-[var(--pt-space-4)]",
            selectedId === node.id ? "bg-[var(--pt-bg-selected)]" : "hover:bg-[var(--pt-bg-hover)]",
          )}
          style={{ paddingLeft: level * 16 }}
        >
          {node.children.length > 0 ? (
            <button
              type="button"
              aria-label={isCollapsed ? "Развернуть" : "Свернуть"}
              onClick={() =>
                setCollapsed((current) => {
                  const next = new Set(current);
                  if (next.has(node.id)) {
                    next.delete(node.id);
                  } else {
                    next.add(node.id);
                  }
                  return next;
                })
              }
              className="text-[var(--pt-text-tertiary)]"
            >
              {isCollapsed ? (
                <ChevronRight className="size-[var(--pt-icon-sm)]" />
              ) : (
                <ChevronDown className="size-[var(--pt-icon-sm)]" />
              )}
            </button>
          ) : (
            <span className="w-[var(--pt-icon-sm)]" />
          )}

          <button
            type="button"
            onClick={() => onSelect(node.id)}
            className={cn(
              "flex-1 truncate text-left text-[var(--pt-font-ui)]",
              inactive && "text-[var(--pt-text-tertiary)] line-through",
            )}
            title={node.is_deleted ? "В корзине" : node.is_archived ? "Завершён" : undefined}
          >
            {node.name}
          </button>

          <DropdownMenu
            trigger={
              <IconButton
                variant="ghost"
                size="sm"
                label={`Действия с проектом ${node.name}`}
                icon={<MoreHorizontal className="size-[var(--pt-icon-sm)]" />}
                // Меню появляется по наведению и по фокусу: иначе до него не
                // добраться с клавиатуры
                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              />
            }
            items={
              node.is_deleted
                ? [
                    {
                      key: "restore",
                      label: "Восстановить из корзины",
                      icon: <RotateCcw className="size-[var(--pt-icon-sm)]" />,
                      onSelect: () => restore.mutate(node.id),
                    },
                  ]
                : [
                    {
                      key: "open",
                      label: "Обзор проекта",
                      icon: <LayoutDashboard className="size-[var(--pt-icon-sm)]" />,
                      onSelect: () => onOpen(node.id),
                    },
                    {
                      key: "rename",
                      label: "Переименовать",
                      icon: <Pencil className="size-[var(--pt-icon-sm)]" />,
                      onSelect: () =>
                        setDraft({ name: node.name, editing: node, parentId: node.parent_id }),
                    },
                    {
                      key: "child",
                      label: "Создать вложенный",
                      icon: <FolderPlus className="size-[var(--pt-icon-sm)]" />,
                      onSelect: () => setDraft({ name: "", editing: null, parentId: node.id }),
                    },
                    {
                      key: "archive",
                      label: node.is_archived ? "Вернуть в работу" : "Завершить проект",
                      icon: <Archive className="size-[var(--pt-icon-sm)]" />,
                      onSelect: () => archive.mutate({ id: node.id, archived: !node.is_archived }),
                    },
                    {
                      key: "delete",
                      label: "Убрать в корзину",
                      icon: <Trash2 className="size-[var(--pt-icon-sm)]" />,
                      danger: true,
                      separatorBefore: true,
                      onSelect: () => {
                        void confirm({
                          title: `Убрать проект «${node.name}» в корзину?`,
                          description:
                            node.children.length > 0
                              ? "Вложенные проекты уйдут вместе с ним. Задачи останутся, но потеряют проект в отборе."
                              : "Задачи останутся, но потеряют проект в отборе. Вернуть можно из корзины.",
                          confirmLabel: "Убрать",
                          tone: "danger",
                        }).then((yes) => {
                          if (yes) {
                            remove.mutate(node.id);
                          }
                        });
                      },
                    },
                  ]
            }
          />
        </div>

        {!isCollapsed && node.children.length > 0 && (
          <ul>{node.children.map((child) => renderNode(child, level + 1))}</ul>
        )}
      </li>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-[var(--pt-space-4)] px-[var(--pt-space-8)] pt-[var(--pt-space-8)]">
        <span className="text-[var(--pt-fs-micro)] uppercase tracking-[var(--pt-ls-micro)] text-[var(--pt-text-tertiary)]">
          Проекты
        </span>
        <IconButton
          className="ml-auto"
          variant="ghost"
          size="sm"
          label="Создать проект"
          icon={<FolderPlus className="size-[var(--pt-icon-sm)]" />}
          onClick={() => setDraft({ name: "", editing: null, parentId: null })}
        />
      </div>

      {all.length >= SEARCH_FROM && (
        // Поиск появляется, когда листать становится дороже, чем набрать
        // три буквы: на пяти проектах поле только занимает место
        <div className="px-[var(--pt-space-8)] pb-[var(--pt-space-4)]">
          <TextField
            aria-label="Найти проект"
            placeholder="Найти проект"
            size="sm"
            value={needle}
            onChange={(event) => setNeedle(event.target.value)}
          />
        </div>
      )}

      {/* flex-1 и min-h-0 вместе: список забирает остаток высоты и прокручивается
          внутри себя. Без flex-1 он растёт по содержимому, и прокрутка достаётся
          полосе навигации, которая содержимое обрезает */}
      <ul className="min-h-0 flex-1 overflow-auto px-[var(--pt-space-4)] pb-[var(--pt-space-4)]">
        <li>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={cn(
              "flex h-[var(--pt-row-h)] w-full items-center rounded-[var(--pt-radius-3)] px-[var(--pt-space-8)]",
              "text-left text-[var(--pt-font-ui)]",
              selectedId === null
                ? "bg-[var(--pt-bg-selected)]"
                : "text-[var(--pt-text-secondary)] hover:bg-[var(--pt-bg-hover)]",
            )}
          >
            Все задачи
          </button>
        </li>
        {tree.map((node) => renderNode(node, 0))}
        {trimmed !== "" && tree.length === 0 && (
          <li className="px-[var(--pt-space-8)] py-[var(--pt-space-8)] text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)]">
            Ничего не нашлось
          </li>
        )}
      </ul>

      <button
        type="button"
        onClick={() => setShowClosed((current) => !current)}
        className={cn(
          "px-[var(--pt-space-8)] pb-[var(--pt-space-8)] text-left",
          "text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)] hover:text-[var(--pt-text-secondary)]",
        )}
      >
        {showClosed ? "Скрыть завершённые и корзину" : "Завершённые и корзина"}
      </button>

      {draft !== null && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setDraft(null);
            }
          }}
          title={draft.editing === null ? "Новый проект" : "Переименовать проект"}
          {...(draft.editing === null && draft.parentId !== null
            ? { description: "Проект будет вложен в выбранный" }
            : {})}
          width={420}
          footer={
            <>
              <Button variant="secondary" onClick={() => setDraft(null)}>
                Отмена
              </Button>
              <Button
                loading={save.isPending}
                disabled={draft.name.trim() === ""}
                onClick={() => save.mutate({ ...draft, name: draft.name.trim() })}
              >
                Сохранить
              </Button>
            </>
          }
        >
          <TextField
            label="Название"
            autoFocus
            value={draft.name}
            onChange={(event) =>
              setDraft((current) =>
                current === null ? current : { ...current, name: event.target.value },
              )
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" && draft.name.trim() !== "") {
                save.mutate({ ...draft, name: draft.name.trim() });
              }
            }}
          />
        </Dialog>
      )}
    </div>
  );
}
